import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Button,
    Card,
    Divider,
    Grid,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { When } from 'react-if';
import { useSelector } from 'react-redux';

import { RootState } from '../../../store';
import { PaginatorFactory, PaginationFactory } from '../../../factory';
import { AppointmentTypes } from '../../../types/appointment.types';
import { AppointmentService } from '../../../services/appointment.service';
import { AppointmentDrawer } from '../../details-drawers/apppointments-drawer';
import { AppointmentsListFilters } from './appointments-filters';
import { InProgressSection } from './in-progress-section';
import { UpcomingSection } from './upcoming-section';
import { PastSummaryCards } from './past-summary-cards';
import { AppointmentStatusCell } from './appointment-status-cell';
import { LocationCell } from './location-cell';
import { deriveLastPingTime } from '../users-list/last-ping-source';
import { PAST_COLUMNS } from '../../../builders/appointments-builders';
import { USER_APPOINTMENT_COLUMNS } from '../../../builders/users-builders';
import { downloadCsv } from '../../../hooks/useCsvDownload';
import Loading from '../../loading';
import NoData from '../../no-data';
import { useEffectiveSettings, SETTING } from '../../../hooks/use-effective-settings';

export const AppointmentsList = () => {
    const { getSetting } = useEffectiveSettings();
    const appointmentsCheckOnMeTrackingEnabled = getSetting(SETTING.appointmentsCheckOnMeTracking, false);

    if (appointmentsCheckOnMeTrackingEnabled) {
        return <EnrichedAppointmentsList />;
    }

    return <LegacyAppointmentsList />;
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Legacy (original) appointments list — flag OFF
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const LegacyAppointmentsList = () => {
    const [selectedAppointment, setSelectedAppointment] = useState<AppointmentTypes.Model | null>(null);
    const [detailsModal, setDetailsModal] = useState<boolean>(false);

    const blankDaysInThePastValue = 0;
    const defaultDaysInThePastValue = 30;
    const [daysInThePast, setDaysInThePast] = useState<number>(defaultDaysInThePastValue);
    const [showUpcomingAppts, setShowUpcomingAppts] = useState<string>('true');
    const [sortUpcoming, setSortUpcoming] = useState<PaginatorFactory<any>['sort']>({ startDateTime: 'asc' });
    const [sortPast, setSortPast] = useState<PaginatorFactory<any>['sort']>({ startDateTime: 'desc' });

    const {
        pagination: { total, items },
        loading,
    } = useSelector((state: RootState) => state.appointment);

    const [pagination, setPagination] = useState<PaginatorFactory<any>>({
        page: 0,
        perPage: 25,
    });

    const onFilter = async (filters: AppointmentTypes.Filters) => {
        await AppointmentService.fetchMany({
            ...pagination,
            page: pagination.page + 1,
            filter: { ...filters },
            sort: showUpcomingAppts === 'true' ? sortUpcoming : sortPast,
        },
        (isValid, msg) => {},
        false,
        showUpcomingAppts === 'true' ? true : false,
        isNaN(daysInThePast) ? blankDaysInThePastValue : daysInThePast);
    };

    const makeCsvDownload = async () => {
        const res: PaginationFactory<AppointmentTypes.Model> = await AppointmentService.fetchMany(
            { perPage: 10000, page: pagination.page + 1, filter: {} },
            undefined,
            true,
            showUpcomingAppts === 'true' ? true : false,
            isNaN(daysInThePast) ? blankDaysInThePastValue : daysInThePast,
        ) as PaginationFactory<AppointmentTypes.Model>;

        const csvArray = res.items?.map((appt) => ({
            startDateTime: appt?.startDateTime ?? '',
            title: appt?.title ?? '',
            notes: appt?.notes ?? '',
            userName: `${appt?.user?.firstName ?? ''} ${appt?.user?.lastName ?? ''}`,
            userPhone: appt?.user?.phoneNumber ?? '',
            location: appt?.location ?? '',
        }));

        const filenameString = showUpcomingAppts === 'true' ? 'upcoming' : 'past'
        downloadCsv(csvArray, `${filenameString}-appointments`);
    }

    useEffect(() => {
        AppointmentService.fetchMany({
            ...pagination,
            page: pagination.page + 1,
            filter: {},
            sort: showUpcomingAppts === 'true' ? sortUpcoming : sortPast,
        },
        (isValid, msg) => {},
        false,
        showUpcomingAppts === 'true' ? true : false,
        isNaN(daysInThePast) ? blankDaysInThePastValue : daysInThePast);
    }, [pagination?.perPage, pagination?.page, sortUpcoming, sortPast, showUpcomingAppts, daysInThePast]);

    return (
        <>
            <Grid container display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ mb: 2 }}>
                <Tabs value={showUpcomingAppts} onChange={(e, v) => {
                    setShowUpcomingAppts(v);
                    setDaysInThePast(defaultDaysInThePastValue);
                }}>
                    <Tab label={"Upcoming"} value={'true'} title="View upcoming appointments"/>
                    <Tab label="Past" value={'false'} title="View previous appointments"/>
                </Tabs>
                {showUpcomingAppts === 'false' && (
                    <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                            Displaying the last
                        </Typography>
                        <Tooltip title={"Days in the past"} placement="top">
                        <TextField
                            type="number"
                            value={daysInThePast}
                            onChange={(e) => {
                                const newValue = Math.max(0, parseInt(e.target.value, 10));
                                setDaysInThePast(newValue);
                            }}
                            inputProps={{ min: 0 }}
                            sx={{ width: 60 }}
                            onBlur={() => {
                                if (isNaN(daysInThePast)) {
                                    setDaysInThePast(blankDaysInThePastValue);
                                }
                            }}
                            onWheel={event => {
                                event.preventDefault();
                            }}
                        />
                        </Tooltip>
                        <Typography variant="body2" sx={{ mr: 1, ml: 1 }}>
                            days
                        </Typography>
                    </Grid>
                )}

            </Grid>
            <Card sx={{ mt: 2 }}>
                <AppointmentsListFilters
                    onFilter={onFilter}
                    onDownload={async () => {
                        await makeCsvDownload();
                    }}
                />
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {USER_APPOINTMENT_COLUMNS?.map((column) => (
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'text.primary' }}
                                        >
                                            {column?.headerName}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {!loading.fetchMany && (items || [])?.map((item) => {
                                return (
                                    <TableRow
                                    sx={{
                                        ":hover": {
                                            backgroundColor: '#F0F0F0',
                                            cursor: 'pointer'
                                        }}
                                    }
                                    key={`appointment_${item.id}`}
                                    onClick={() => {
                                        setSelectedAppointment(item);
                                        setDetailsModal(true);
                                    }}
                                >
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            fontWeight={400}
                                            sx={{ color: 'text.primary' }}
                                        >
                                            {item?.startDateTime ? dayjs(item?.startDateTime).format('MM/DD/YYYY hh:mm A') : 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            fontWeight={400}
                                            sx={{ color: 'text.primary' }}
                                        >
                                            {item?.title || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            fontWeight={400}
                                            sx={{ color: 'text.primary' }}
                                        >
                                            {item?.notes || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            fontWeight={400}
                                            sx={{ color: 'text.primary' }}
                                        >
                                            {item?.user?.firstName} {item?.user?.lastName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            fontWeight={400}
                                            sx={{ color: 'text.primary' }}
                                        >
                                            {item?.user?.phoneNumber || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>

                    <When condition={loading?.fetchMany}>
                        <Loading />
                    </When>

                    <When
                        condition={
                            loading.fetchMany === false && items?.length < 1
                        }
                    >
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title={'No appointments found'}
                            description={
                                'No appointments have been created.'
                            }
                        />
                    </When>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component={"div"}
                    count={total ?? 0}
                    rowsPerPage={pagination?.perPage}
                    page={pagination?.page}
                    onPageChange={(e, p) =>
                        setPagination((pag) => ({ ...pag, page: p }))
                    }
                    onRowsPerPageChange={(e) =>
                        setPagination((pag) => ({
                            ...pag,
                            perPage: parseInt(e.target.value, 10),
                        }))
                    }
                />
            </Card>

            <AppointmentDrawer
                visible={detailsModal}
                onClose={() => {
                    setDetailsModal(false);
                    setSelectedAppointment(null);
                }}
                selectedAppointment={selectedAppointment!}
            />
        </>
    )
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Enriched (redesign) appointments list — flag ON
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

type ActiveTab = 'active' | 'past';

interface FilterState {
    daysInThePast: number;
    customStartDate?: string;
    customEndDate?: string;
    statusFilter: string;
    filter: AppointmentTypes.Filters;
}

const DEFAULT_FILTER_STATE: FilterState = {
    daysInThePast: 30,
    customStartDate: undefined,
    customEndDate: undefined,
    statusFilter: 'all',
    filter: {} as AppointmentTypes.Filters,
};

const EnrichedAppointmentsList = () => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState<ActiveTab>('active');
    const [selectedAppointment, setSelectedAppointment] = useState<AppointmentTypes.Model | null>(null);
    const [detailsModal, setDetailsModal] = useState(false);
    const [filterState, setFilterState] = useState<FilterState>(DEFAULT_FILTER_STATE);
    const [fetchError, setFetchError] = useState<string | null>(null);

    const {
        enrichedPagination: { total, items },
        summary,
        loading,
    } = useSelector((state: RootState) => state.appointment);

    const [pagination, setPagination] = useState<PaginatorFactory<any>>({
        page: 0,
        perPage: 25,
    });

    // ─── Data fetching ────────────────────────────────────────────

    const fetchData = async () => {
        setFetchError(null);
        const tab = activeTab === 'active' ? 'activeFuture' : 'past';
        const result = await AppointmentService.fetchEnriched(
            {
                ...pagination,
                page: pagination.page + 1,
                filter: { ...filterState.filter },
                sort: activeTab === 'active'
                    ? { startDateTime: 'asc' }
                    : { startDateTime: 'desc' },
            },
            tab,
            filterState.daysInThePast,
            activeTab === 'past' ? filterState.statusFilter : undefined,
            undefined,
            false,
            filterState.customStartDate,
            filterState.customEndDate,
        );
        if (!result) {
            setFetchError('Failed to load appointments. Please try again.');
        }
    };

    useEffect(() => {
        fetchData();
    }, [pagination.page, pagination.perPage, activeTab, filterState]);

    // ─── Handlers ─────────────────────────────────────────────────

    const onFilter = async (filters: AppointmentTypes.Filters) => {
        setPagination((p) => ({ ...p, page: 0 }));

        // Consolidate all filter state into a single update to prevent cascading re-fetches
        setFilterState((prev) => {
            const next: FilterState = {
                ...prev,
                filter: filters,
                statusFilter: filters.status ?? 'all',
            };

            if (filters.timePeriod === 'custom') {
                next.daysInThePast = 0;
                next.customStartDate = filters.startDate || undefined;
                next.customEndDate = filters.endDate || undefined;
            } else if (filters.timePeriod) {
                next.daysInThePast = parseInt(filters.timePeriod, 10) || 30;
                next.customStartDate = undefined;
                next.customEndDate = undefined;
            }

            return next;
        });
    };

    const onRowClick = (appointment: AppointmentTypes.Model) => {
        setSelectedAppointment(appointment);
        setDetailsModal(true);
    };

    const makeCsvDownload = async () => {
        const tab = activeTab === 'active' ? 'activeFuture' : 'past';
        const res = await AppointmentService.fetchEnriched(
            { perPage: 10000, page: 1, filter: { ...filterState.filter } },
            tab,
            filterState.daysInThePast,
            activeTab === 'past' ? filterState.statusFilter : undefined,
            undefined,
            true,
            filterState.customStartDate,
            filterState.customEndDate,
        );

        if (!res) return;

        if (activeTab === 'active') {
            const csvArray = res.items?.map((appt) => {
                const lastPing = deriveLastPingTime(appt.user, [appt.lastDevicePing]);
                return {
                    scheduledTime: appt.startDateTime ? dayjs(appt.startDateTime).format('MM/DD/YYYY hh:mm A') : '',
                    title: appt.title ?? '',
                    userName: `${appt.user?.firstName ?? ''} ${appt.user?.lastName ?? ''}`,
                    location: appt.location ?? '',
                    checkIn: appt.checkIn ? dayjs(appt.checkIn).format('MM/DD/YYYY hh:mm A') : '',
                    status: appt.liveStatus ?? '',
                    lastPing: lastPing ? dayjs(lastPing).format('MM/DD/YYYY hh:mm A') : '',
                };
            });
            downloadCsv(csvArray, 'active-future-appointments');
        } else {
            const csvArray = res.items?.map((appt) => ({
                scheduledTime: appt.startDateTime ? dayjs(appt.startDateTime).format('MM/DD/YYYY hh:mm A') : '',
                title: appt.title ?? '',
                userName: `${appt.user?.firstName ?? ''} ${appt.user?.lastName ?? ''}`,
                location: appt.location ?? '',
                checkInLocation: appt.checkInLocation?.address ?? '',
                checkIn: appt.checkIn ? dayjs(appt.checkIn).format('MM/DD/YYYY hh:mm A') : '',
                checkOut: appt.checkOut ? dayjs(appt.checkOut).format('MM/DD/YYYY hh:mm A') : '',
                checkOutLocation: appt.checkOutLocation?.address ?? '',
                status: appt.pastStatus ?? '',
            }));
            downloadCsv(csvArray, 'past-appointments');
        }
    };

    // ─── Split active items into In Progress + Upcoming ───────────

    const inProgressItems = useMemo(() => {
        if (activeTab !== 'active' || !items) return [];
        return items.filter((a) => a.isInProgress);
    }, [items, activeTab]);

    const upcomingItems = useMemo(() => {
        if (activeTab !== 'active' || !items) return [];
        return items.filter((a) => !a.isInProgress);
    }, [items, activeTab]);

    // ─── Render ───────────────────────────────────────────────────

    return (
        <>
            {/* Tab bar */}
            <Box sx={{ mb: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => {
                        setActiveTab(v as ActiveTab);
                        setPagination((p) => ({ ...p, page: 0 }));
                        setFilterState(DEFAULT_FILTER_STATE);
                    }}
                >
                    <Tab
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: theme.palette.success.main,
                                        animation: 'tabPulse 2s ease-in-out infinite',
                                        '@keyframes tabPulse': {
                                            '0%, 100%': { opacity: 1 },
                                            '50%': { opacity: 0.4 },
                                        },
                                    }}
                                />
                                Active & Future
                            </Box>
                        }
                        value="active"
                    />
                    <Tab label="Past" value="past" />
                </Tabs>
            </Box>

            <Card>
                {/* Filters */}
                <AppointmentsListFilters
                    onFilter={onFilter}
                    onDownload={makeCsvDownload}
                    activeTab={activeTab}
                />

                {/* Loading */}
                <When condition={loading?.fetchEnriched}>
                    <Loading />
                </When>

                {/* Error state */}
                <When condition={!loading?.fetchEnriched && !!fetchError}>
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="error" sx={{ mb: 1 }}>{fetchError}</Typography>
                        <Button variant="outlined" size="small" onClick={fetchData}>
                            Retry
                        </Button>
                    </Box>
                </When>

                {/* Active & Future tab */}
                <When condition={activeTab === 'active' && !loading?.fetchEnriched && !fetchError}>
                    <>
                        <InProgressSection
                            items={inProgressItems}
                            onRowClick={onRowClick}
                        />

                        {inProgressItems.length > 0 && upcomingItems.length > 0 && (
                            <Divider sx={{ borderBottomWidth: 2, mx: 2 }} />
                        )}

                        <UpcomingSection
                            items={upcomingItems}
                            onRowClick={onRowClick}
                        />

                        {(!items || items.length === 0) && (
                            <NoData
                                buttonText={null}
                                onClick={() => {}}
                                title="No appointments found"
                                description="There are no active or upcoming appointments."
                            />
                        )}
                    </>
                </When>

                {/* Past tab */}
                <When condition={activeTab === 'past' && !loading?.fetchEnriched && !fetchError}>
                    <>
                        <Box sx={{ p: 2 }}>
                            <PastSummaryCards summary={summary ?? null} />
                        </Box>

                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        {PAST_COLUMNS.map((col) => (
                                            <TableCell key={col.field}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'text.primary', fontWeight: 600 }}
                                                >
                                                    {col.headerName}
                                                </Typography>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(items || []).map((appt) => (
                                        <TableRow
                                            key={appt.id}
                                            sx={{
                                                ':hover': {
                                                    backgroundColor: theme.palette.action.hover,
                                                    cursor: 'pointer',
                                                },
                                            }}
                                            onClick={() => onRowClick(appt)}
                                        >
                                            {/* Scheduled Time */}
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {appt.startDateTime
                                                        ? dayjs(appt.startDateTime).format('MM/DD/YYYY h:mm A')
                                                        : '--'}
                                                </Typography>
                                            </TableCell>

                                            {/* Title */}
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {appt.title || '--'}
                                                </Typography>
                                            </TableCell>

                                            {/* User Name */}
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {appt.user?.firstName && appt.user?.lastName
                                                        ? `${appt.user.firstName} ${appt.user.lastName}`
                                                        : '[Deleted User]'}
                                                </Typography>
                                            </TableCell>

                                            {/* Appt Location */}
                                            <TableCell>
                                                <LocationCell location={appt.location || (appt.coordinates && (appt.coordinates.latitude !== 0 || appt.coordinates.longitude !== 0) ? `${appt.coordinates.latitude}, ${appt.coordinates.longitude}` : undefined)} />
                                            </TableCell>

                                            {/* Checked-In Location */}
                                            <TableCell>
                                                <LocationCell
                                                    location={appt.checkInLocation}
                                                    isGps={!!appt.checkIn}
                                                />
                                            </TableCell>

                                            {/* Check-In */}
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {appt.checkIn
                                                        ? dayjs(appt.checkIn).format('h:mm A')
                                                        : '--'}
                                                </Typography>
                                            </TableCell>

                                            {/* Check-Out */}
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {appt.checkOut
                                                        ? dayjs(appt.checkOut).format('h:mm A')
                                                        : '--'}
                                                </Typography>
                                            </TableCell>

                                            {/* Status */}
                                            <TableCell>
                                                <AppointmentStatusCell
                                                    status={appt.pastStatus || 'no-checkin'}
                                                    variant="past"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            {(!items || items.length === 0) && (
                                <NoData
                                    buttonText={null}
                                    onClick={() => {}}
                                    title="No past appointments found"
                                    description="There are no past appointments in the selected time period."
                                />
                            )}
                        </TableContainer>
                    </>
                </When>

                {/* Pagination (both tabs) */}
                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={total ?? 0}
                    rowsPerPage={pagination.perPage}
                    page={pagination.page}
                    onPageChange={(_, p) => setPagination((pag) => ({ ...pag, page: p }))}
                    onRowsPerPageChange={(e) =>
                        setPagination((pag) => ({
                            ...pag,
                            perPage: parseInt(e.target.value, 10),
                        }))
                    }
                />
            </Card>

            {/* Detail drawer */}
            <AppointmentDrawer
                visible={detailsModal}
                onClose={() => {
                    setDetailsModal(false);
                    setSelectedAppointment(null);
                }}
                selectedAppointment={selectedAppointment!}
            />
        </>
    );
};
